import React, { useEffect } from 'react';
import { Source, Layer, useMap } from 'react-map-gl';
import type { Expression } from 'mapbox-gl';
import {
  CAMERA_LABEL_EXPRESSION,
  CAMERA_OVERLAY_LAYER_IDS,
  type CameraFeatureCollection,
} from '@signals/app-shell/services/cameraOverlayService';
import type { CameraMarkerSize } from '@signals/app-shell/hooks/useCameraLocationsOverlay';
import { CAMERA_CLUSTER_ICON, CAMERA_ICON_PREFIX, installCameraIcons } from './cameraMapIcons';

type Props = {
  data: CameraFeatureCollection;
  visible: boolean;
  /** Group nearby cameras into count bubbles (default true). */
  cluster?: boolean;
  /** Show camera names under markers once zoomed in (default false). */
  showLabels?: boolean;
  /** Marker size preset (default small). */
  size?: CameraMarkerSize;
};

/** `camera-icon-<category>`; unknown slugs are rasterised on demand via styleimagemissing. */
const CATEGORY_ICON_EXPRESSION = [
  'concat',
  CAMERA_ICON_PREFIX,
  ['coalesce', ['get', 'category'], 'other'],
] as unknown as Expression;

/** Glyph scale (icon-size) by zoom, per size preset. Base glyph is 24 CSS px. */
const ICON_SCALE: Record<CameraMarkerSize, { z8: number; z12: number; z16: number }> = {
  small: { z8: 0.4, z12: 0.55, z16: 0.8 },
  medium: { z8: 0.55, z12: 0.75, z16: 1.05 },
  large: { z8: 0.75, z12: 1.0, z16: 1.35 },
};

/** Zoom from which camera names are drawn (matches the "zoom 13+" hint in the layer dialogs). */
export const LABEL_MIN_ZOOM = 13;

const CLUSTER_SCALE: Record<CameraMarkerSize, [number, number, number]> = {
  small: [0.8, 0.95, 1.1],
  medium: [0.95, 1.1, 1.3],
  large: [1.1, 1.3, 1.5],
};

/** Registers the camera glyph images with the map (and re-adds after style swaps). */
function useCameraIcons(active: boolean) {
  const { current: map } = useMap();
  useEffect(() => {
    const m = map?.getMap?.();
    if (!active || !m) return undefined;
    return installCameraIcons(m);
  }, [map, active]);
}

/**
 * Camera location markers shared by Pathfinder and COP maps.
 * Cameras are drawn as a video-camera glyph tinted by category so they can't
 * be confused with device-track circles; clusters are a larger neutral glyph
 * with a count badge.
 *
 * `cluster` toggles the GeoJSON source's clustering, which Mapbox only reads
 * at source creation — hence the `key` to remount the Source when it flips.
 */
const CameraLocationsMapLayers: React.FC<Props> = ({
  data,
  visible,
  cluster = true,
  showLabels = false,
  size = 'small',
}) => {
  const active = visible && data.features.length > 0;
  useCameraIcons(active);
  if (!active) return null;
  const s = ICON_SCALE[size] || ICON_SCALE.small;
  const c = CLUSTER_SCALE[size] || CLUSTER_SCALE.small;
  // Labels are drawn only on unclustered points, so while labels are on the
  // clusters give way one step before LABEL_MIN_ZOOM; otherwise toggling
  // "Labels" did nothing until zoom 15 on a dense city layer.
  const clusterMaxZoom = showLabels ? LABEL_MIN_ZOOM - 1 : 14;

  return (
    <Source
      key={`camera-src-${cluster ? 'clustered' : 'flat'}-${clusterMaxZoom}`}
      id={CAMERA_OVERLAY_LAYER_IDS.source}
      type="geojson"
      data={data}
      cluster={cluster}
      clusterMaxZoom={clusterMaxZoom}
      clusterRadius={45}
    >
      {cluster && (
        <Layer
          id={CAMERA_OVERLAY_LAYER_IDS.clusters}
          type="symbol"
          filter={['has', 'point_count']}
          layout={{
            'icon-image': CAMERA_CLUSTER_ICON,
            'icon-size': ['step', ['get', 'point_count'], c[0], 25, c[1], 100, c[2]],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            // Count badge at the glyph's top-right corner.
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
            'text-size': 10,
            'text-anchor': 'bottom-left',
            'text-offset': [0.55, -0.45],
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          }}
          paint={{
            'text-color': '#ffffff',
            'text-halo-color': '#0b0f14',
            'text-halo-width': 1.5,
          }}
        />
      )}
      <Layer
        id={CAMERA_OVERLAY_LAYER_IDS.unclustered}
        type="symbol"
        filter={['!', ['has', 'point_count']]}
        layout={{
          'icon-image': CATEGORY_ICON_EXPRESSION,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 8, s.z8, 12, s.z12, 16, s.z16],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        }}
        paint={{ 'icon-opacity': 0.95 }}
      />
      <Layer
        id={CAMERA_OVERLAY_LAYER_IDS.labels}
        type="symbol"
        filter={['!', ['has', 'point_count']]}
        minzoom={LABEL_MIN_ZOOM}
        layout={{
          visibility: showLabels ? 'visible' : 'none',
          // Road / intersection label when derived, else the supplied name.
          'text-field': CAMERA_LABEL_EXPRESSION as unknown as Expression,
          'text-size': 10,
          'text-offset': [0, 1.1],
          'text-anchor': 'top',
          'text-max-width': 10,
          'text-allow-overlap': false,
          'text-optional': true,
        }}
        paint={{
          // Neutral black-on-white halo: the category colour is already carried by
          // the marker, and coloured text was hard to read over the basemap.
          'text-color': '#111111',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.6,
          'text-halo-blur': 0.2,
        }}
      />
    </Source>
  );
};

export default CameraLocationsMapLayers;
